import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MessageCircle, ChevronLeft, ChevronRight, BadgePercent } from 'lucide-react';
import { ProductEntity } from '../../../domain/entities/product.entity';
import { Badge } from './badge';
import { useCatalog } from '../../hooks/use-catalog';

interface ProductCardProps {
  product: ProductEntity;
  onContactClick?: (product: ProductEntity) => void;
}

const getProductImages = (imagesList: string[], fallbackUrl: string): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  const list = imagesList.length > 0 ? imagesList : [fallbackUrl];
  for (const url of list) {
    if (!url) continue;
    const clean = url.trim();
    const key = clean.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      result.push(clean);
    }
  }
  return result.length > 0 ? result : [fallbackUrl];
};

export const ProductCard: React.FC<ProductCardProps> = ({ product, onContactClick }) => {
  const { trackProductInquiryUseCase } = useCatalog();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const rawImages = (product.isPromotion && product.promoAndProductImages.length > 0)
    ? product.promoAndProductImages
    : product.images;
  const images = getProductImages(rawImages, product.imageUrl);
  const hasMultiple = images.length > 1;

  const handlePrevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handleSelectImage = (idx: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(idx);
  };

  const whatsappMessage = encodeURIComponent(
    `Hola BARTECH, me interesa consultar disponibilidad y cotización del equipo: ${product.name} (${product.brand} - ${product.model}).`
  );

  return (
    <div className="group bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-3 sm:p-4 flex flex-col justify-between hover:border-[#33CC00]/70 dark:hover:border-[#33CC00]/60 hover:shadow-[0_8px_20px_rgba(51,204,0,0.12)] transition-all duration-200">
      <div>
        {/* Imagen del Hardware con Carrusel de Previsualización */}
        <div className="relative aspect-square w-full bg-neutral-50 dark:bg-neutral-800/60 rounded-lg p-3 sm:p-4 flex items-center justify-center overflow-hidden mb-3.5 border border-neutral-100 dark:border-neutral-800/40">
          <Link to={`/catalogo/${product.slug}`} className="w-full h-full flex items-center justify-center">
            <img
              src={images[currentImageIndex] || product.imageUrl}
              alt={`${product.name} - Vista ${currentImageIndex + 1}`}
              className="w-full h-full object-contain rounded-md group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </Link>

          {/* Badges superiores */}
          <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1 pointer-events-none z-10">
            {product.isPromotion && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-amber-500 text-white shadow-sm animate-pulse">
                <BadgePercent className="w-3 h-3" />
                PROMOCIÓN
              </span>
            )}
            {product.isFeatured && (
              <Badge variant="brand" size="sm">
                Destacado POS
              </Badge>
            )}
          </div>

          {/* Contador de fotos */}
          {hasMultiple && (
            <div className="absolute top-2.5 right-2.5 px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[10px] font-mono font-bold text-white z-10 pointer-events-none">
              {currentImageIndex + 1}/{images.length}
            </div>
          )}

          {/* Flechas de Navegación de Imágenes (Visibles en hover) */}
          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={handlePrevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 dark:bg-neutral-800/90 text-neutral-700 dark:text-neutral-200 hover:text-brand-primary shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 z-20 hover:scale-110 cursor-pointer"
                aria-label="Foto anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 dark:bg-neutral-800/90 text-neutral-700 dark:text-neutral-200 hover:text-brand-primary shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 z-20 hover:scale-110 cursor-pointer"
                aria-label="Siguiente foto"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}

          {/* Barras Indicadoras de Cantidad de Fotos (Estilo Stories / Modern E-commerce) */}
          {hasMultiple && (
            <div className="absolute bottom-2.5 left-0 right-0 flex justify-center items-center gap-1 px-3 z-20">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => handleSelectImage(idx, e)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentImageIndex
                      ? 'w-5 bg-brand-primary shadow-sm'
                      : 'w-1.5 bg-neutral-400/60 dark:bg-neutral-600/70 hover:bg-neutral-500'
                  }`}
                  aria-label={`Ver foto ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Metadata de Marca y Categoría */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            {product.brand}
          </span>
          <span className="text-[10px] text-neutral-400 dark:text-neutral-500 font-mono">
            {product.model}
          </span>
        </div>

        {/* Nombre del Producto */}
        <h3 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-white line-clamp-2 group-hover:text-brand-primary transition-colors">
          <Link to={`/catalogo/${product.slug}`} className="focus:outline-none focus-visible:underline">
            {product.name}
          </Link>
        </h3>

        {product.isPromotion && product.promotionTitle && (
          <div className="mt-1.5 flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/60 text-[11px] font-semibold">
            <BadgePercent className="w-3.5 h-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="truncate">{product.promotionTitle}</span>
          </div>
        )}

        {/* Descripción Técnica Breve */}
        <p className="text-xs text-neutral-600 dark:text-neutral-400 line-clamp-2 mt-1.5 leading-relaxed">
          {product.shortDescription}
        </p>

        {/* Chips de Especificaciones Técnicas y Conectividad */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {product.screenSize && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-900/50">
              {product.screenSize}
            </span>
          )}
          {product.ram && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-900/50">
              {product.ram} RAM
            </span>
          )}
          {product.operatingSystem && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50">
              {product.operatingSystem}
            </span>
          )}
          {product.scannerType && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50">
              {product.scannerType}
            </span>
          )}
          {product.interfaces && product.interfaces.slice(0, 2).map((iface) => (
            <span
              key={iface}
              className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700/60"
            >
              {iface}
            </span>
          ))}
        </div>
      </div>

      {/* Acciones del Producto */}
      <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-2">
        <Link
          to={`/catalogo/${product.slug}`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:text-brand-primary dark:hover:text-brand-primary transition-colors"
        >
          <span>Ficha Técnica</span>
          <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>

        <a
          href={`https://wa.me/56971380593?text=${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackProductInquiryUseCase?.execute(product.id, 'catalog_card').catch(() => {});
            onContactClick?.(product);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-[#33CC00]/15 text-[#1a7f00] dark:text-[#33CC00] hover:bg-[#33CC00] hover:text-white transition-all duration-150 border border-[#33CC00]/40 shadow-xs"
          title="Consultar por WhatsApp a un especialista"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span>Consultar</span>
        </a>
      </div>
    </div>
  );
};
